/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Contact page: aligned with Features, About, Pricing (MarketingPageHero,
 * MarketingSectionHeader, standard container). Single contact email, no fake phone/address.
 */

"use client";

import { useState, useCallback } from "react";
import { Footer } from "@/components/landing/footer";
import { MarketingPageHero } from "@/components/landing/marketing-page-hero";
import { MarketingCTASection } from "@/components/landing/marketing-cta-section";
import { FadeIn } from "@/components/landing/primitives/fade-in";
import MainNavigation from "@/components/layout/main-navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EMAILS } from "@/lib/constants/master";
import { CheckCircle, Loader2, Mail, MessageSquare, Send } from "lucide-react";

const CONTACT_EMAIL = EMAILS.CONTACT;

const contactReasons = [
  {
    icon: MessageSquare,
    title: "Sales & demos",
    description: "Learn about pricing, features, or schedule a walkthrough.",
  },
  {
    icon: Mail,
    title: "Support & questions",
    description: "Help with the platform, integrations, or general questions.",
  },
];

type ContactFormState = "idle" | "loading" | "success" | "error";

function ContactForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [formState, setFormState] = useState<ContactFormState>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!email || !email.includes("@")) {
        setErrorMessage("Please enter a valid email address");
        setFormState("error");
        return;
      }

      if (!message.trim()) {
        setErrorMessage("Please enter a message");
        setFormState("error");
        return;
      }

      setFormState("loading");
      setErrorMessage("");

      try {
        const response = await fetch("/api/marketing/leads", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            ...(name && { name }),
            ...(company && { company }),
            message: message.trim(),
            source: "contact_form" as const,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error?.message || "Something went wrong");
        }

        setFormState("success");
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : "Something went wrong",
        );
        setFormState("error");
      }
    },
    [email, name, company, message],
  );

  if (formState === "success") {
    return (
      <div className="text-center py-8">
        <div className="w-14 h-14 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-7 h-7 text-secondary" />
        </div>
        <h3 className="text-xl font-bold text-foreground mb-2">
          Message sent!
        </h3>
        <p className="text-muted-foreground">
          We&apos;ll get back to you within 24 hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label
            htmlFor="contact-name"
            className="block text-sm font-medium text-foreground mb-1.5"
          >
            Name
          </label>
          <Input
            id="contact-name"
            type="text"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={formState === "loading"}
            className="bg-background"
          />
        </div>
        <div>
          <label
            htmlFor="contact-email"
            className="block text-sm font-medium text-foreground mb-1.5"
          >
            Email *
          </label>
          <Input
            id="contact-email"
            type="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (formState === "error") {
                setFormState("idle");
                setErrorMessage("");
              }
            }}
            disabled={formState === "loading"}
            className="bg-background"
            required
          />
        </div>
      </div>

      <div>
        <label
          htmlFor="contact-company"
          className="block text-sm font-medium text-foreground mb-1.5"
        >
          Company{" "}
          <span className="text-muted-foreground font-normal">(optional)</span>
        </label>
        <Input
          id="contact-company"
          type="text"
          placeholder="Company name"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          disabled={formState === "loading"}
          className="bg-background"
        />
      </div>

      <div>
        <label
          htmlFor="contact-message"
          className="block text-sm font-medium text-foreground mb-1.5"
        >
          Message *
        </label>
        <textarea
          id="contact-message"
          placeholder="How can we help?"
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            if (formState === "error") {
              setFormState("idle");
              setErrorMessage("");
            }
          }}
          disabled={formState === "loading"}
          rows={4}
          required
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-foreground text-sm placeholder:text-muted-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:opacity-50 resize-none"
        />
      </div>

      {formState === "error" && errorMessage && (
        <p className="text-sm text-destructive" role="alert">
          {errorMessage}
        </p>
      )}

      <Button
        type="submit"
        disabled={formState === "loading" || !email || !message.trim()}
        className="w-full bg-secondary hover:bg-secondary/90 text-white font-medium"
        size="lg"
      >
        {formState === "loading" ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            Send Message
          </>
        )}
      </Button>

      <p className="text-xs text-center text-muted-foreground">
        We respond to every message — usually within 24 hours.
      </p>
    </form>
  );
}

export function ContactPageClient() {
  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />

      <MarketingPageHero
        variant="dark"
        badge="Contact"
        title={
          <>
            Get in{" "}
            <span className="bg-gradient-to-br from-secondary to-secondary/80 bg-clip-text text-transparent">
              touch
            </span>
          </>
        }
        description="Have a question about Contractor Co-Pilot? We're here to help. Reach out for sales, support, or to get started."
      />

      {/* Contact Form + Methods */}
      <section
        className="py-16 md:py-24 lg:py-32 bg-background-warm"
        aria-labelledby="contact-form-heading"
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-16">
          <div className="mx-auto max-w-3xl grid gap-8 lg:grid-cols-5">
            {/* Form */}
            <FadeIn className="lg:col-span-3">
              <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
                <h2
                  id="contact-form-heading"
                  className="text-xl font-bold text-foreground font-display mb-1"
                >
                  Send us a message
                </h2>
                <p className="text-sm text-muted-foreground mb-6">
                  Tell us what you need and we&apos;ll get back to you.
                </p>
                <ContactForm />
              </div>
            </FadeIn>

            {/* Sidebar */}
            <FadeIn className="lg:col-span-2 space-y-4" delay={100}>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="group flex items-start gap-3 rounded-2xl border border-border bg-muted/30 p-5 transition-all duration-300 hover:border-secondary/20 hover:shadow-lg"
              >
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary text-white"
                  aria-hidden
                >
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-foreground font-display mb-1">
                    Email us directly
                  </h3>
                  <span className="text-sm font-medium text-secondary group-hover:underline">
                    {CONTACT_EMAIL}
                  </span>
                </div>
              </a>

              {contactReasons.map((item, index) => {
                const Icon = item.icon;
                return (
                  <FadeIn
                    key={item.title}
                    as="article"
                    delay={(index + 2) * 100}
                    className="rounded-2xl border border-border bg-card p-5"
                  >
                    <div
                      className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"
                      aria-hidden
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="text-sm font-bold text-foreground mb-1">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </FadeIn>
                );
              })}
            </FadeIn>
          </div>
        </div>
      </section>

      <MarketingCTASection
        title="Ready to get started?"
        description="Get started today. No long-term contracts."
        primaryCTA={{ text: "Start Your Free Trial", href: "/auth/sign-up" }}
        secondaryCTA={{
          text: "Email us",
          href: `mailto:${CONTACT_EMAIL}`,
        }}
        trustIndicators={["We respond within 24 hours"]}
      />

      <Footer />
    </div>
  );
}
